// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type { Appointment, Encounter, Patient } from '@medplum/fhirtypes';
import type { AppointmentTypeDefinition, CancelAppointmentResult } from '@polarissleep/scheduling';
import {
  cancelAppointment as cancelLegacyAppointment,
  REPLACED_BY_APPOINTMENT_EXTENSION_URL,
} from '@polarissleep/scheduling';
import { isV2Appointment } from '@polarissleep/scheduling-next';
import * as Sentry from '@sentry/react';
import { cancelAppointmentThroughBot } from '../scheduling-next/patient-appointment-bot';
import type { PatientAppointmentCandidate } from '../scheduling-next/types';
import { isPatientBookableStart } from '../utils/patient-scheduling-notice';
import { bookV2Appointment } from './booking-service';

export interface PatientV2RescheduleResult {
  newAppointmentId: string;
  newEncounterId: string;
  oldAppointmentId: string;
  oldSlotReleased: boolean;
  oldSlotsRestored: number;
}

export async function cancelPatientAppointment(
  medplum: MedplumClient,
  appointmentId: string,
  reason?: string
): Promise<CancelAppointmentResult> {
  const appointment = await medplum.readResource('Appointment', appointmentId);
  if (isV2Appointment(appointment) && appointment.status === 'fulfilled') {
    throw new Error(`Cannot cancel fulfilled Appointment/${appointmentId}`);
  }
  if (appointment.status === 'cancelled') {
    return { appointmentId, slotReleased: false, slotsRestored: 0, encounterCancelled: false };
  }
  if (!isV2Appointment(appointment)) {
    return cancelLegacyAppointment(medplum, appointmentId, reason);
  }

  await cancelAppointmentThroughBot(medplum, appointmentId, reason);
  const encounterCancelled = await cancelLinkedEncounter(medplum, appointmentId);
  return { appointmentId, slotReleased: false, slotsRestored: 0, encounterCancelled };
}

export async function reschedulePatientV2Appointment(
  medplum: MedplumClient,
  params: {
    appointmentId: string;
    patient: Patient;
    appointmentType: AppointmentTypeDefinition;
    candidate: PatientAppointmentCandidate;
  }
): Promise<PatientV2RescheduleResult> {
  const oldAppointment = await medplum.readResource('Appointment', params.appointmentId);
  if (oldAppointment.status !== 'booked') {
    throw new Error('Only booked appointments can be rescheduled.');
  }
  if (!isPatientBookableStart(params.candidate.appointment.start)) {
    throw new Error('Patients cannot book tomorrow. Please choose a date at least two calendar days from today.');
  }

  const booked = await bookV2Appointment(medplum, {
    patient: params.patient,
    candidate: params.candidate,
    appointmentType: params.appointmentType,
    replacesAppointmentId: params.appointmentId,
  });
  try {
    await medplum.updateResource<Appointment>({
      ...oldAppointment,
      extension: [
        ...(oldAppointment.extension ?? []).filter(
          (extension) => extension.url !== REPLACED_BY_APPOINTMENT_EXTENSION_URL
        ),
        {
          url: REPLACED_BY_APPOINTMENT_EXTENSION_URL,
          valueReference: { reference: `Appointment/${booked.appointmentId}` },
        },
      ],
    });
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        appointmentId: params.appointmentId,
        newAppointmentId: booked.appointmentId,
        operation: 'link-replacement',
      },
    });
  }

  let oldSlotReleased = false;
  let oldSlotsRestored = 0;
  try {
    const cancelled = await cancelPatientAppointment(medplum, params.appointmentId, 'Rescheduled');
    oldSlotReleased = cancelled.slotReleased;
    oldSlotsRestored = cancelled.slotsRestored;
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        appointmentId: params.appointmentId,
        newAppointmentId: booked.appointmentId,
        operation: 'v2-cancel-old',
      },
    });
  }

  return {
    newAppointmentId: booked.appointmentId,
    newEncounterId: booked.encounterId,
    oldAppointmentId: params.appointmentId,
    oldSlotReleased,
    oldSlotsRestored,
  };
}

async function cancelLinkedEncounter(medplum: MedplumClient, appointmentId: string): Promise<boolean> {
  const encounters = await medplum.searchResources('Encounter', {
    appointment: `Appointment/${appointmentId}`,
    _count: '1',
  });
  const encounter = encounters[0] as Encounter | undefined;
  if (!encounter?.id || encounter.status === 'cancelled' || encounter.status === 'finished') {
    return false;
  }
  try {
    await medplum.updateResource<Encounter>({ ...encounter, status: 'cancelled' });
    return true;
  } catch (error) {
    Sentry.captureException(error, { extra: { appointmentId, operation: 'patient-v2-cancel-encounter' } });
    return false;
  }
}
